import React, { useEffect } from "react";
import { Circle, MapContainer, Marker, Polygon, TileLayer, useMap, useMapEvents } from "react-leaflet";
import L from "leaflet";
import { C } from "../theme";
import Badge from "../components/Badge";
import Button from "../components/Button";
import Card from "../components/Card";
import CollapsibleSection from "../components/CollapsibleSection";
import PulseDot from "../components/PulseDot";
import ProfileAvatar from "../components/ProfileAvatar";
import { getProfilePhotoUrl } from "../services/profilePhoto";
import { useSessionContext } from "../context/SessionContext";
import { db } from "../firebase";
import { collection, onSnapshot, orderBy, query } from "firebase/firestore";
import useTideStage from "../hooks/useTideStage";
import useMoonPhase from "../hooks/useMoonPhase";
import useSolunarMajor from "../hooks/useSolunarMajor";
import useWaterTemp from "../hooks/useWaterTemp";
import useWind from "../hooks/useWind";
import useBarometricPressure from "../hooks/useBarometricPressure";
import { buildConditionsSnapshot } from "../utils/conditionsSnapshot";
import { isLocationInsideGeofence } from "../utils/geofence";
import { triggerTournamentAlertFeedback } from "../services/tournamentAlertFeedback";

// Watermark overlays on the satellite map are always on a dark background.
// Use theme-independent colors so the labels remain legible in light mode
// (where C.white inverts to a dark tone and disappears on dark tiles).
const OVERLAY = {
  text: "#FFFFFF",
  muted: "#B8C5D0",
  red: "#E03030",
  textShadow: "0 1px 2px rgba(0,0,0,0.85), 0 0 6px rgba(0,0,0,0.55)",
};

export default function RecordingScreen({ onCatch, onMissedFish, onEnd, onBack, elapsed, sessionResumed = false, onVideo, location, geoStatus, userProfile, tournamentGeofence, tournamentDates, soundAlerts = true, vibrationAlerts = true }) {
  const { userId, sessionId, endSession, logConditionSnapshot, lastConditionLogAt, lastCatchId } = useSessionContext();
  const [sessionCatches, setSessionCatches] = React.useState([]);
  const [showSessionCatches, setShowSessionCatches] = React.useState(false);
  const [exportPrompt, setExportPrompt] = React.useState(null); // { email } | null
  const [endingTrip, setEndingTrip] = React.useState(false);

  // Show the TOURNAMENT chip on the Video Proof button only when the angler
  // is enrolled in a tournament AND the current time falls within the
  // tournament's published start/end window.
  const isTournamentLive = React.useMemo(() => {
    const enrolled = Boolean(
      userProfile?.inTournament || userProfile?.tournamentId || userProfile?.tournamentName
    );
    if (!enrolled) return false;
    const startMs = tournamentDates?.startDate ? new Date(tournamentDates.startDate).getTime() : null;
    const endRawMs = tournamentDates?.endDate ? new Date(tournamentDates.endDate).getTime() : null;
    if (!startMs && !endRawMs) return false;
    // If the published end date is exactly midnight UTC, treat as end-of-day.
    let endMs = endRawMs;
    if (endRawMs) {
      const d = new Date(endRawMs);
      const isMidnightUtc =
        d.getUTCHours() === 0 && d.getUTCMinutes() === 0 &&
        d.getUTCSeconds() === 0 && d.getUTCMilliseconds() === 0;
      if (isMidnightUtc) endMs = endRawMs + 24 * 60 * 60 * 1000 - 1;
    }
    const now = Date.now();
    if (startMs && now < startMs) return false;
    if (endMs && now > endMs) return false;
    return true;
  }, [userProfile?.inTournament, userProfile?.tournamentId, userProfile?.tournamentName, tournamentDates?.startDate, tournamentDates?.endDate]);
  const mapCenter = location ? [location.lat, location.lng] : [29.5, -88.5];
  const mapRef = React.useRef(null);
  const initialFixCenteredRef = React.useRef(false);
  const pulseIcon = React.useMemo(() => L.divIcon({
    className: "",
    html: `<div style="width:16px;height:16px;border-radius:50%;background:${C.accent};border:2.5px solid white;box-shadow:0 0 14px ${C.accent}cc;"></div>`,
    iconSize: [16, 16],
    iconAnchor: [8, 8],
  }), []);

  // Auto-center only on the first GPS fix; afterwards, the angler is free
  // to pan/zoom around the map (e.g. to inspect the boundary). A dedicated
  // recenter button restores the GPS-locked view on demand.
  function MapController({ lat, lng }) {
    const map = useMap();
    useEffect(() => {
      mapRef.current = map;
      return () => {
        if (mapRef.current === map) mapRef.current = null;
      };
    }, [map]);
    useEffect(() => {
      if (!lat || !lng) return;
      if (initialFixCenteredRef.current) return;
      map.setView([lat, lng]);
      initialFixCenteredRef.current = true;
    }, [lat, lng, map]);
    useMapEvents({
      // No-op, but registering events ensures the panel mounts properly.
    });
    return null;
  }

  const handleRecenterMap = () => {
    if (!mapRef.current || !location) return;
    mapRef.current.setView([location.lat, location.lng], mapRef.current.getZoom());
  };
  const tide = useTideStage({
    lat: location?.lat,
    lng: location?.lng,
    enabled: !!location,
  });
  const moon = useMoonPhase(location);
  const solunarMajor = useSolunarMajor(location);
  const waterTemp = useWaterTemp(location, "F");
  const wind = useWind(location);
  const barometric = useBarometricPressure(location);
  const latestSnapshotRef = React.useRef(null);
  const [geofenceBreachActive, setGeofenceBreachActive] = React.useState(false);
  const previousBreachRef = React.useRef(false);
  const soundAlertsRef = React.useRef(soundAlerts);
  const vibrationAlertsRef = React.useRef(vibrationAlerts);

  React.useEffect(() => { soundAlertsRef.current = !!soundAlerts; }, [soundAlerts]);
  React.useEffect(() => { vibrationAlertsRef.current = !!vibrationAlerts; }, [vibrationAlerts]);

  const currentSnapshot = React.useMemo(
    () =>
      buildConditionsSnapshot({
        moon,
        solunarMajor,
        waterTemp,
        wind,
        barometric,
        tideStage: tide.tideStage,
        nextTide: tide.nextTide,
      }),
    [moon, solunarMajor, waterTemp, wind, barometric, tide.tideStage, tide.nextTide]
  );

  useEffect(() => {
    latestSnapshotRef.current = currentSnapshot;
  }, [currentSnapshot]);

  useEffect(() => {
    const hasLocation = Boolean(location && Number.isFinite(location.lat) && Number.isFinite(location.lng));
    const hasGeofence = Boolean(tournamentGeofence);

    if (!sessionId || !userId || !hasGeofence || !hasLocation || !isTournamentLive) {
      setGeofenceBreachActive(false);
      previousBreachRef.current = false;
      return;
    }

    const inside = isLocationInsideGeofence(location, tournamentGeofence);
    const breach = !inside;
    setGeofenceBreachActive(breach);

    if (breach && !previousBreachRef.current) {
      triggerTournamentAlertFeedback({
        eventType: "geofence_breach",
        sound: soundAlertsRef.current,
        vibrate: vibrationAlertsRef.current,
      });
    }
    previousBreachRef.current = breach;
  }, [
    location,
    sessionId,
    tournamentGeofence,
    userId,
    isTournamentLive,
  ]);

  useEffect(() => {
    if (!sessionId) return undefined;

    let cancelled = false;
    const writeSnapshot = async () => {
      if (cancelled) return;
      const snapshot = latestSnapshotRef.current;
      await logConditionSnapshot(snapshot, location);
    };

    writeSnapshot();
    const interval = setInterval(writeSnapshot, 15 * 60 * 1000);

    return () => {
      cancelled = true;
      clearInterval(interval);
    };
  }, [sessionId, logConditionSnapshot, location]);

  useEffect(() => {
    if (!userId || !sessionId) {
      setSessionCatches([]);
      return undefined;
    }

    const catchesRef = collection(db, "users", userId, "sessions", sessionId, "catches");
    const catchesQuery = query(catchesRef, orderBy("caughtAt", "desc"));

    const unsubscribe = onSnapshot(
      catchesQuery,
      (snapshot) => {
        // Use "estimate" so docs whose serverTimestamp() hasn't been confirmed
        // yet still surface a usable caughtAt value (otherwise newly-saved
        // catches return null and sort to the bottom of desc order, causing
        // the previous catch to be selected for video metadata).
        const next = snapshot.docs.map((item) => {
          const data = item.data({ serverTimestamps: "estimate" }) || {};
          const caughtAtDate = data.caughtAt?.toDate?.() ?? null;
          const size = typeof data.lengthIn === "number"
            ? `${data.lengthIn.toFixed(1).replace(/\.0$/, "")}"`
            : "—";
          const weight = typeof data.weightLbs === "number"
            ? `${data.weightLbs.toFixed(1).replace(/\.0$/, "")} lbs`
            : "—";
          const lure = data.lure || "—";
          const species = data.species || "Unknown";
          const time = caughtAtDate
            ? new Intl.DateTimeFormat(undefined, {
              hour: "numeric",
              minute: "2-digit",
            }).format(caughtAtDate)
            : "—";

          return {
            id: item.id,
            species,
            size,
            weight,
            lure,
            time,
            outcome: data.outcome === "missed" ? "missed" : "landed",
            caughtAtMs: caughtAtDate ? caughtAtDate.getTime() : 0,
          };
        });
        next.sort((a, b) => (b.caughtAtMs || 0) - (a.caughtAtMs || 0));
        setSessionCatches(next);
      },
      (error) => {
        console.error("[Recording] failed to load session catches:", error);
        setSessionCatches([]);
      }
    );

    return () => unsubscribe();
  }, [sessionId, userId]);

  const safeElapsed = Math.max(0, Math.floor(elapsed || 0));
  const hrs = Math.floor(safeElapsed / 3600);
  const mins = Math.floor((safeElapsed % 3600) / 60);
  const secs = safeElapsed % 60;

  const gpsLabel =
    geoStatus === "available"
      ? location
        ? `${location.lat.toFixed(5)}, ${location.lng.toFixed(5)}`
        : "acquiring…"
      : geoStatus === "denied"
      ? "permission denied"
      : geoStatus === "unsupported"
      ? "unsupported"
      : "waiting…";
  const gpsIsAcquiring = geoStatus === "requesting" || (geoStatus === "available" && !location);

  const handleVideoProof = () => {
    const targetCatch = catches[0] || null;
    // For the FIRST catch of a session the catches snapshot can still be empty
    // at the moment the user taps "Video Proof" (Firestore hasn't delivered the
    // just-logged catch yet). Fall back to the synchronously-tracked
    // lastCatchId so the video links to that catch instead of uploading as an
    // orphan that never appears in the trip review.
    const targetCatchId = targetCatch?.id || lastCatchId || null;
    onVideo({
      catchId: targetCatchId,
      sessionId: sessionId || null,
      catchData: {
        species: targetCatch?.species || "—",
        size: targetCatch?.size || "—",
        weight: targetCatch?.weight || "—",
        lure: targetCatch?.lure || "—",
      },
    });
  };

  const finalizeEnd = React.useCallback(async (shouldSendExportEmail) => {
    if (endingTrip) return;
    setEndingTrip(true);
    const profileEmail = String(userProfile?.email || "").trim();
    const isTournamentSession = Boolean(
      userProfile?.inTournament || userProfile?.tournamentId || userProfile?.tournamentName
    );
    try {
      const endedSessionId = await endSession({
        exportEmail: {
          shouldSend: shouldSendExportEmail,
          userEmail: profileEmail || null,
          isTournamentSession,
          tournamentId: userProfile?.tournamentId || null,
          tournamentName: userProfile?.tournamentName || null,
          teamId: userProfile?.teamId || null,
          teamName: userProfile?.teamName || null,
        },
      });
      onEnd(endedSessionId || sessionId || null);
    } finally {
      setEndingTrip(false);
      setExportPrompt(null);
    }
  }, [endSession, endingTrip, onEnd, sessionId, userProfile]);

  const handleEnd = () => {
    const profileEmail = String(userProfile?.email || "").trim();
    const isTournamentSession = Boolean(
      userProfile?.inTournament || userProfile?.tournamentId || userProfile?.tournamentName
    );

    if (isTournamentSession) {
      finalizeEnd(true);
      return;
    }
    if (profileEmail) {
      setExportPrompt({ email: profileEmail });
      return;
    }
    finalizeEnd(false);
  };

  const catches = sessionCatches;
  // Landed fish only — missed fish are shown in the list but excluded from the
  // "fish logged" tally so the count reflects actual catches.
  const landedCount = sessionCatches.filter((c) => c.outcome !== "missed").length;

  const lastSessionLogLabel = React.useMemo(() => {
    if (!sessionId || !lastConditionLogAt) return "not active";
    return new Intl.DateTimeFormat(undefined, {
      hour: "numeric",
      minute: "2-digit",
    }).format(new Date(lastConditionLogAt));
  }, [sessionId, lastConditionLogAt]);

  const tideLabel = tide.loading
    ? "Loading…"
    : tide.error
    ? "Unavailable"
    : tide.tideStage || "—";
  const tempLabel = waterTemp.label || "—";
  const windLabel = wind.label || "—";
  const baroLabel = barometric.label || "—";

  return (
    <div className="hide-scrollbar" style={{ flex: 1, overflowY: "auto", paddingBottom: 24, position: "relative" }}>
      <div
        style={{
          background: "#160808",
          padding: "13px 18px",
          display: "flex",
          flexDirection: "column",
          gap: 6,
          borderBottom: "1px solid #3a1212",
        }}
      >
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 10 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
            <PulseDot />
            <span style={{ fontSize: 11, color: C.red, fontWeight: 700, letterSpacing: 2 }}>SESSION LOGGING IN PROGRESS</span>
          </div>
          <ProfileAvatar
            url={getProfilePhotoUrl(userProfile)}
            name={[userProfile?.firstName, userProfile?.lastName].filter(Boolean).join(" ")}
            size={24}
            title="You"
            style={{ marginRight: 36 }}
          />
        </div>
        <div style={{ marginLeft: 20, display: "flex", alignItems: "center", gap: 8 }}>
          <Badge color={C.red}>LIVE</Badge>
          {sessionResumed && (
            <span
              role="status"
              aria-live="polite"
              title="This trip was resumed from a previous login — the timer reflects the real time since it started."
              style={{
                display: "inline-flex",
                alignItems: "center",
                gap: 4,
                padding: "2px 8px",
                borderRadius: 999,
                background: "rgba(245,158,11,0.16)",
                border: "1px solid rgba(251,191,36,0.6)",
                color: "#fbbf24",
                fontSize: 9.5,
                fontWeight: 800,
                letterSpacing: 1.2,
                textTransform: "uppercase",
              }}
            >
              <span aria-hidden="true" style={{ fontSize: 11, lineHeight: 1 }}>↻</span>
              Resumed from previous login
            </span>
          )}
        </div>
        <div style={{ fontSize: 20, fontWeight: 800, color: C.white, fontFamily: "Georgia,serif", letterSpacing: 2, alignSelf: "flex-end" }}>
          {String(hrs).padStart(2, "0")}:{String(mins).padStart(2, "0")}:{String(secs).padStart(2, "0")}
        </div>
      </div>
      <div
        style={{
          background: "#160808",
          borderBottom: "1px solid #3a1212",
          padding: "6px 18px 8px",
          fontSize: 10,
          color: C.muted,
          letterSpacing: 1,
        }}
      >
        Last Session Log: {lastSessionLogLabel}
      </div>

      {geofenceBreachActive && (
        <div
          style={{
            margin: "10px 14px 0",
            borderRadius: 10,
            border: "1px solid #ff8a00",
            background: "linear-gradient(150deg,#5a2a00,#3a1400)",
            padding: "9px 11px",
            color: "#ffd7a3",
          }}
        >
          <div style={{ fontSize: 11, fontWeight: 800, letterSpacing: 1.1, textTransform: "uppercase" }}>
            Tournament Area Restriction Breach
          </div>
          <div style={{ fontSize: 10, marginTop: 4, lineHeight: 1.35 }}>
            Return inside the approved tournament zone. A server event is created at session start and on each 15-minute conditions update while you remain outside the boundary.
          </div>
        </div>
      )}

      <div
        style={{
          margin: "14px 14px 0",
          borderRadius: 12,
          overflow: "hidden",
          border: `1px solid ${C.border}`,
          height: 322,
          position: "relative",
          background: "#06192e",
        }}
      >
        <MapContainer
          center={mapCenter}
          zoom={location ? 15 : 6}
          style={{ position: "absolute", inset: 0, width: "100%", height: "100%", zIndex: 0 }}
          scrollWheelZoom={true}
          attributionControl={false}
        >
          <TileLayer
            url="https://services.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}"
          />
          <MapController lat={location?.lat} lng={location?.lng} />
          {location?.lat && location?.lng && (
            <Marker position={[location.lat, location.lng]} icon={pulseIcon} />
          )}
          {tournamentGeofence?.type === "polygon" && tournamentGeofence.polygon?.length >= 3 && (
            <Polygon
              positions={tournamentGeofence.polygon.map((p) => [p.lat, p.lng])}
              pathOptions={{
                color: geofenceBreachActive ? "#ff4444" : "#3B82F6",
                weight: 2,
                opacity: 0.85,
                fillColor: geofenceBreachActive ? "#ff4444" : "#3B82F6",
                fillOpacity: 0.08,
              }}
            />
          )}
          {tournamentGeofence?.type === "circle" && tournamentGeofence.center && tournamentGeofence.radiusMeters > 0 && (
            <Circle
              center={[tournamentGeofence.center.lat, tournamentGeofence.center.lng]}
              radius={tournamentGeofence.radiusMeters}
              pathOptions={{
                color: geofenceBreachActive ? "#ff4444" : "#3B82F6",
                weight: 2,
                opacity: 0.85,
                fillColor: geofenceBreachActive ? "#ff4444" : "#3B82F6",
                fillOpacity: 0.08,
              }}
            />
          )}
        </MapContainer>
        <button
          type="button"
          onClick={handleRecenterMap}
          disabled={!location}
          title="Re-center on my location"
          aria-label="Re-center on my location"
          style={{
            position: "absolute",
            bottom: 10,
            right: 10,
            zIndex: 3,
            width: 36,
            height: 36,
            borderRadius: 18,
            border: `1px solid ${C.border}`,
            background: "rgba(6,25,46,0.92)",
            color: location ? C.accent : OVERLAY.muted,
            cursor: location ? "pointer" : "not-allowed",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            fontSize: 16,
            boxShadow: "0 2px 6px rgba(0,0,0,0.4)",
          }}
        >
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
            <circle cx="12" cy="12" r="3" />
            <line x1="12" y1="2" x2="12" y2="5" />
            <line x1="12" y1="19" x2="12" y2="22" />
            <line x1="2" y1="12" x2="5" y2="12" />
            <line x1="19" y1="12" x2="22" y2="12" />
          </svg>
        </button>
        <div
          style={{
            position: "absolute",
            top: 10,
            left: 0,
            right: 0,
            display: "flex",
            justifyContent: "center",
            zIndex: 2,
            pointerEvents: "none",
          }}
        >
          <div
            style={{
              padding: "6px 10px",
              borderRadius: 10,
              border: `1px solid ${OVERLAY.red}aa`,
              background: "rgba(10,12,18,0.78)",
              boxShadow: `inset 0 0 0 1px ${OVERLAY.red}55, 0 2px 8px rgba(0,0,0,0.5)`,
              color: OVERLAY.text,
              textShadow: OVERLAY.textShadow,
              fontSize: 10,
              letterSpacing: 0.8,
              fontWeight: 700,
              textAlign: "center",
              textTransform: "uppercase",
            }}
          >
            GPS: {gpsLabel}
          </div>
        </div>
        {gpsIsAcquiring && (
          <div
            style={{
              position: "absolute",
              top: 10,
              right: 12,
              zIndex: 2,
              padding: "4px 8px",
              borderRadius: 8,
              border: `1px solid ${C.border}`,
              background: "rgba(6,20,36,0.92)",
              boxShadow: "0 2px 6px rgba(0,0,0,0.45)",
              color: OVERLAY.muted,
              textShadow: OVERLAY.textShadow,
              fontSize: 9,
              letterSpacing: 0.5,
              textTransform: "uppercase",
            }}
          >
            GPS signal weak / waiting…
          </div>
        )}
        <div
          style={{
            position: "absolute",
            inset: 0,
            display: "flex",
            alignItems: "flex-start",
            justifyContent: "center",
            pointerEvents: "none",
            zIndex: 2,
            paddingTop: 46,
          }}
        >
          <div
            style={{
              padding: "6px 10px",
              borderRadius: 10,
              border: `1px solid ${OVERLAY.red}aa`,
              background: "rgba(10,12,18,0.78)",
              boxShadow: `inset 0 0 0 1px ${OVERLAY.red}55, 0 2px 8px rgba(0,0,0,0.5)`,
              color: OVERLAY.text,
              textShadow: OVERLAY.textShadow,
              fontSize: 10,
              letterSpacing: 0.8,
              fontWeight: 700,
              textAlign: "center",
              textTransform: "uppercase",
            }}
          >
            Aim at target and select Video Proof
          </div>
        </div>
        <div
          style={{
            position: "absolute",
            bottom: 10,
            left: 12,
            fontSize: 10,
            color: OVERLAY.text,
            background: "rgba(10,12,18,0.78)",
            border: `1px solid ${C.border}`,
            borderRadius: 8,
            padding: "3px 8px",
            textShadow: OVERLAY.textShadow,
            boxShadow: "0 2px 6px rgba(0,0,0,0.45)",
          }}
        >
          {landedCount} fish logged
        </div>
      </div>

      <div style={{ margin: "10px 14px", display: "flex", gap: 8 }}>
        {[
          ["TIDE", tideLabel],
          ["TEMP", tempLabel],
          ["WIND", windLabel],
          ["BARO", baroLabel],
        ].map(([l, v]) => (
          <div
            key={l}
            style={{
              flex: 1,
              background: C.cardBg,
              borderRadius: 8,
              padding: "7px 6px",
              border: `1px solid ${C.border}`,
              textAlign: "center",
            }}
          >
            <div style={{ fontSize: 8, color: C.teal, letterSpacing: 1.5, marginBottom: 2 }}>{l}</div>
            <div style={{ fontSize: 10, color: C.white, fontWeight: 700 }}>{v}</div>
          </div>
        ))}
      </div>

      {catches.length > 0 && (
        <div style={{ margin: "4px 14px 10px" }}>
          <CollapsibleSection
            open={showSessionCatches}
            onToggle={() => setShowSessionCatches(!showSessionCatches)}
            headerTitle={showSessionCatches ? "Hide catches" : "Show catches"}
            toggleTestId="recording-session-catches-toggle"
            title={<span style={{ fontSize: 10, color: C.muted, letterSpacing: 2 }}>CATCHES THIS SESSION</span>}
          >
            <div className="hide-scrollbar" style={{ maxHeight: 140, overflowY: "auto", paddingRight: 2 }}>
              {catches.map((c, index) => (
                <div
                  key={c.id}
                  id={`recording-session-catch-${index}`}
                  data-testid={`recording-session-catch-${index}`}
                >
                  <Card style={{ padding: "10px 12px", marginBottom: 6 }}>
                    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                      <div>
                        <div style={{ fontSize: 13, fontWeight: 700, color: C.white, fontFamily: "Georgia,serif" }}>{c.species}</div>
                        <div style={{ fontSize: 10, color: C.muted, marginTop: 2 }}>
                          {c.outcome === "missed" ? c.lure : `${c.size} · ${c.weight} · ${c.lure}`}
                        </div>
                      </div>
                      <div style={{ textAlign: "right" }}>
                        <div style={{ fontSize: 10, color: C.gold }}>{c.time}</div>
                        <Badge color={c.outcome === "missed" ? C.warning : C.seafoam} style={{ marginTop: 3 }}>
                          {c.outcome === "missed" ? "MISSED" : "LOGGED"}
                        </Badge>
                      </div>
                    </div>
                  </Card>
                </div>
              ))}
            </div>
        </CollapsibleSection>
        </div>
      )}

      <div style={{ margin: "0 14px 8px", display: "flex", gap: 10 }}>
        <Button
          id="recording-log-catch"
          data-testid="recording-log-catch"
          onClick={onCatch}
          variant="primary"
          style={{
            flex: 1,
            flexDirection: "column",
            gap: 2,
            padding: "11px 0",
            boxShadow: `0 6px 24px ${C.seafoam}40`,
          }}
        >
          <div style={{ fontSize: 14, fontWeight: 800, color: "#FFFFFF", letterSpacing: 0.5 }}>Log Catch</div>
          <div style={{ fontSize: 9, color: "rgba(255,255,255,0.7)", letterSpacing: 2 }}>VOICE</div>
        </Button>

        <Button
          id="recording-video-proof"
          data-testid="recording-video-proof"
          onClick={handleVideoProof}
          variant="primary"
          style={{
            flex: 1,
            flexDirection: "column",
            gap: 2,
            padding: "11px 0",
            boxShadow: `0 6px 24px ${C.seafoam}40`,
            position: "relative",
            overflow: "hidden",
          }}
        >
          <div
            style={{
              position: "absolute",
              top: 0,
              right: 0,
              background: C.gold,
              padding: "2px 8px",
              borderBottomLeftRadius: 9,
              fontSize: 7,
              fontWeight: 800,
              color: "#0A1628",
              letterSpacing: 1,
              display: isTournamentLive ? "block" : "none",
            }}
          >
            TOURNAMENT
          </div>
          <div style={{ fontSize: 14, fontWeight: 800, color: "#FFFFFF", letterSpacing: 0.5 }}>Video Proof</div>
          <div style={{ fontSize: 9, color: "rgba(255,255,255,0.7)", letterSpacing: 2 }}>CERTIFIED</div>
        </Button>
      </div>
      {onMissedFish && (
        <div style={{ margin: "0 14px 8px" }}>
          <Button
            id="recording-missed-fish"
            data-testid="recording-missed-fish"
            onClick={onMissedFish}
            variant="secondary"
            fullWidth
            style={{
              padding: "9px 0",
              border: `1px solid ${C.warning}66`,
            }}
          >
            <span style={{ fontSize: 13, fontWeight: 800, color: C.warning, letterSpacing: 0.5 }}>Missed Fish</span>
          </Button>
        </div>
      )}
      <div style={{ margin: "0 14px" }}>
        <Button
          id="recording-end-trip"
          data-testid="recording-end-trip"
          onClick={handleEnd}
          variant="danger"
          fullWidth
          style={{
            flexDirection: "column",
            gap: 2,
            padding: "11px 0",
            boxShadow: `0 6px 24px ${C.red}40`,
          }}
        >
          <div style={{ fontSize: 14, fontWeight: 800, color: "#FFFFFF", letterSpacing: 1 }}>END TRIP</div>
          <div style={{ fontSize: 10, color: "rgba(255,255,255,0.7)", letterSpacing: 2, marginTop: 2 }}>FINALIZE & ANALYZE</div>
        </Button>
      </div>
      {exportPrompt && (
        <div
          onClick={() => { if (!endingTrip) setExportPrompt(null); }}
          style={{
            position: "fixed",
            inset: 0,
            zIndex: 300,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            background: "rgba(0,0,0,0.55)",
            padding: "0 18px",
            boxSizing: "border-box",
          }}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            role="dialog"
            aria-modal="true"
            aria-labelledby="end-trip-export-title"
            style={{
              width: "min(100%, 340px)",
              background: C.cardBg,
              borderRadius: 16,
              border: `1px solid ${C.border}`,
              boxShadow: C.menuShadow,
              overflow: "hidden",
              display: "flex",
              flexDirection: "column",
            }}
          >
            <div
              style={{
                padding: "16px 18px 10px",
                borderBottom: `1px solid ${C.border}`,
              }}
            >
              <div style={{ fontSize: 10, color: C.gold, letterSpacing: 3, marginBottom: 4 }}>
                END TRIP
              </div>
              <div
                id="end-trip-export-title"
                style={{ fontSize: 16, fontWeight: 700, color: C.white, fontFamily: "Georgia,serif" }}
              >
                Send catch export?
              </div>
            </div>
            <div style={{ padding: "14px 18px 18px" }}>
              <div style={{ fontSize: 13, color: C.white, lineHeight: 1.5, marginBottom: 6 }}>
                Email a copy of this session's catches to:
              </div>
              <div
                style={{
                  fontSize: 13,
                  color: C.gold,
                  fontWeight: 600,
                  wordBreak: "break-all",
                  marginBottom: 18,
                }}
              >
                {exportPrompt.email}
              </div>
              <div style={{ display: "flex", gap: 10 }}>
                <Button
                  onClick={() => finalizeEnd(false)}
                  disabled={endingTrip}
                  variant="ghost"
                  size="sm"
                  style={{ flex: 1, letterSpacing: 1.5 }}
                >
                  Skip
                </Button>
                <Button
                  onClick={() => finalizeEnd(true)}
                  disabled={endingTrip}
                  variant="primary"
                  size="sm"
                  style={{ flex: 1, letterSpacing: 1.5, boxShadow: `0 6px 18px ${C.seafoam}40` }}
                >
                  {endingTrip ? "Sending…" : "Send"}
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
